// src/components/Layout.jsx
import { Outlet, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import './Layout.css'; // opcional para estilos
import CookieConsent from './CookieConsent';
import ModalProvider from './ModalProvider';
import Breadcrumbs from './Breadcrumbs';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';
import * as analytics from '../utils/analytics';

export default function Layout() {
  const location = useLocation();
  const { lang } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  // Ensure lang is a valid language code; protect against cases where path like '/login' sets lang='login'
  const safeLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : (i18n.language || DEFAULT_LANGUAGE);
  const isItinerario = location.pathname.endsWith('/itinerario');
  const isAuthPage = location.pathname.endsWith('/login') || location.pathname.endsWith('/register')
    || location.pathname.endsWith('/forgot-password') || location.pathname.includes('/reset-password')
    || location.pathname.includes('/verify-email');
  const [menuOpen, setMenuOpen] = useState(false);
  // isMobile se gestiona via matchMedia (nunca lee window.innerWidth en render
  // para evitar forced reflow). Se actualiza reactivamente con el evento 'change'.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 900px)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // 📊 ANALYTICS: Track traffic source and organic landing (only once on mount)
  useEffect(() => {
    analytics.saveTrafficSource();
    analytics.trackOrganicLanding();
  }, []);

  // 📊 ANALYTICS: Track scroll depth (per page)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let scrollPercentages = new Set();
    
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const percentage = Math.round((scrolled / documentHeight) * 100);
      
      // Trackear en milestones
      if (percentage >= 25 && !scrollPercentages.has(25)) {
        scrollPercentages.add(25);
        analytics.trackScrollDepth(25);
      }
      if (percentage >= 50 && !scrollPercentages.has(50)) {
        scrollPercentages.add(50);
        analytics.trackScrollDepth(50);
      }
      if (percentage >= 75 && !scrollPercentages.has(75)) {
        scrollPercentages.add(75);
        analytics.trackScrollDepth(75);
      }
      if (percentage >= 99 && !scrollPercentages.has(100)) {
        scrollPercentages.add(100);
        analytics.trackScrollDepth(100);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // 📊 ANALYTICS: Track time on page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const entryTime = Date.now();
    
    return () => {
      const exitTime = Date.now();
      const timeSpent = Math.floor((exitTime - entryTime) / 1000);
      analytics.trackTimeOnPage(timeSpent);
    };
  }, [location.pathname]);

  // Keep i18n.language in sync with the URL lang param
  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      // Always sync with URL param to ensure consistency after page refresh
      i18n.changeLanguage(lang);
      try { window.localStorage.setItem('i18nextLng', lang); } catch (err) { /* ignore */ }
    }
  }, [lang]);

  // Refs to detect outside clicks
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Cambiar idioma y ruta
  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    const oldLang = i18n.language;
    
    // 📊 ANALYTICS: Track language change
    const pageType = location.pathname.includes('/blog/') ? 'blog' : 
                     location.pathname.includes('/itinerario') ? 'itinerary' : 'homepage';
    analytics.trackLanguageChange(oldLang, newLang, pageType);
    
    i18n.changeLanguage(newLang);
    try { window.localStorage.setItem('i18nextLng', newLang); } catch (err) { /* ignore */ }
    // Cambia solo el prefijo de idioma en la ruta actual
    const newPath = location.pathname.replace(/^\/(es|en|de|fr|it|pt|nl|sv|da|el|fi)/, '/' + newLang);
    navigate(newPath + location.search, { replace: true });
  };

  // Hreflang links para SEO
  // Hreflang links are generated globally by SeoMeta; keep Layout lean

  // Autenticación real
  const [isLogged, setIsLogged] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      setIsLogged(false);
      return;
    }
    
    const apiLang = ['es', 'en'].includes(i18n.language) ? i18n.language : 'es';
    api.get(`/${apiLang}/me`)
      .then(() => setIsLogged(true))
      .catch(() => setIsLogged(false));
  }, [location, i18n.language]);

  const handleUserClick = () => {
    if (isLogged) {
      navigate(`/${safeLang}/viajes`);
    } else {
      navigate(`/${safeLang}/login`);
    }
  };

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close when clicking outside or pressing Escape
  useEffect(() => {
    function handleDocClick(e) {
      const navEl = navRef.current;
      const burgerEl = hamburgerRef.current;
      if (!menuOpen) return;
      if (navEl && !navEl.contains(e.target) && burgerEl && !burgerEl.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  return (
    <>
      <Helmet>
        {/* SeoMeta handles canonical and hreflang tags globally */}
      </Helmet>
      <ModalProvider>
      <div className={`layout${isItinerario ? ' layout--itinerario' : ''}`}>
        <CookieConsent />
        {/* HEADER */}
        <header className="header">
          {/* Agrupamos hamburger + logo para control responsive */}
          <div className="header-left">
            {/* Botón hamburguesa solo visible en móvil */}
            <button
              ref={hamburgerRef}
              className="hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
            <div className="logo" onClick={() => navigate(`/${safeLang}`)} style={{ cursor: 'pointer' }}>
              <picture>
                <source srcSet="/logo_planiaro.webp" type="image/webp" />
                <img src="/logo_planiaro.png" alt="Planiaro - Planificador de viajes con IA gratis" width="150" height="50" />
              </picture>
            </div>
          </div>

          <nav ref={navRef} className={`nav${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen && isMobile}>
            <Link onClick={() => setMenuOpen(false)} className={location.pathname === `/${safeLang}` ? 'active' : ''} to={`/${safeLang}`}>{t('inicio', 'Inicio')}</Link>
            <Link onClick={() => setMenuOpen(false)} className={location.pathname === `/${safeLang}/planificador-ia` ? 'active' : ''} to={`/${safeLang}/planificador-ia`}>{t('sobre_nosotros')}</Link>
            <Link onClick={() => setMenuOpen(false)} className={location.pathname === `/${safeLang}/blog` ? 'active' : ''} to={`/${safeLang}/blog`}>{t('blog', 'Blog')}</Link>
            <Link onClick={() => setMenuOpen(false)} className={location.pathname === `/${safeLang}/contact` ? 'active' : ''} to={`/${safeLang}/contact`}>{t('contacto', 'Contacto')}</Link>
            {isLogged && (
              <Link onClick={() => setMenuOpen(false)} className={location.pathname === `/${safeLang}/dashboard` ? 'active' : ''} to={`/${safeLang}/dashboard`}>
                {t('dashboard_profile', 'Perfil')}
              </Link>
            )}
          </nav>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={handleUserClick}
              className="header-user-btn"
              title={isLogged ? t('cuenta', 'Cuenta') : t('login', 'Login')}>
              <span style={{fontSize: 16, display: 'flex', alignItems: 'center', lineHeight: 1}}>
                {isLogged ? <i className={'bi bi-compass'} /> : <i className={'bi bi-box-arrow-in-right'} />}
              </span>
              <span style={{lineHeight: 1}}>{isLogged ? t('mis_viajes', 'Mis Viajes') : t('login', 'Iniciar sesión')}</span>
            </button>
            <div className="lang-select-wrapper">
            <Select
              id="lang-select"
              instanceId="lang-select"
              aria-label="Seleccionar idioma"
              classNamePrefix="lang-select"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              blurInputOnSelect
              onMenuClose={() => {
                if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
              options={[
                { value: 'da', label: <span><span role="img" aria-label="Dansk">🇩🇰</span> Dansk</span> },
                { value: 'de', label: <span><span role="img" aria-label="Deutsch">🇩🇪</span> Deutsch</span> },
                { value: 'el', label: <span><span role="img" aria-label="Ελληνικά">🇬🇷</span> Ελληνικά</span> },
                { value: 'en', label: <span><span role="img" aria-label="English">🇬🇧</span> English</span> },
                { value: 'es', label: <span><span role="img" aria-label="Español">🇪🇸</span> Español</span> },
                { value: 'fi', label: <span><span role="img" aria-label="Suomi">🇫🇮</span> Suomi</span> },
                { value: 'fr', label: <span><span role="img" aria-label="Français">🇫🇷</span> Français</span> },
                { value: 'it', label: <span><span role="img" aria-label="Italiano">🇮🇹</span> Italiano</span> },
                { value: 'nl', label: <span><span role="img" aria-label="Nederlands">🇳🇱</span> Nederlands</span> },
                { value: 'pt', label: <span><span role="img" aria-label="Português">🇵🇹</span> Português</span> },
                { value: 'sv', label: <span><span role="img" aria-label="Svenska">🇸🇪</span> Svenska</span> }
              ]}
              value={{
                value: safeLang,
                label:
                  safeLang === 'es' ? <span><span role="img" aria-label="Español">🇪🇸</span> Español</span> :
                  safeLang === 'en' ? <span><span role="img" aria-label="English">🇬🇧</span> English</span> :
                  safeLang === 'de' ? <span><span role="img" aria-label="Deutsch">🇩🇪</span> Deutsch</span> :
                  safeLang === 'fr' ? <span><span role="img" aria-label="Français">🇫🇷</span> Français</span> :
                  safeLang === 'it' ? <span><span role="img" aria-label="Italiano">🇮🇹</span> Italiano</span> :
                  safeLang === 'pt' ? <span><span role="img" aria-label="Português">🇵🇹</span> Português</span> :
                  safeLang === 'nl' ? <span><span role="img" aria-label="Nederlands">🇳🇱</span> Nederlands</span> :
                  safeLang === 'sv' ? <span><span role="img" aria-label="Svenska">🇸🇪</span> Svenska</span> :
                  safeLang === 'da' ? <span><span role="img" aria-label="Dansk">🇩🇰</span> Dansk</span> :
                  safeLang === 'el' ? <span><span role="img" aria-label="Ελληνικά">🇬🇷</span> Ελληνικά</span> :
                  safeLang === 'fi' ? <span><span role="img" aria-label="Suomi">🇫🇮</span> Suomi</span> :
                  safeLang
              }}
              onChange={opt => handleLanguageChange({ target: { value: opt.value } })}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: 40,
                  borderRadius: 12,
                  borderColor: state.isFocused ? '#7C3AED' : '#D1D5DB',
                  boxShadow: state.isFocused ? '0 0 0 2px rgba(124,58,237,0.15)' : 'none',
                  background: '#fff',
                  fontSize: 13.5,
                  paddingLeft: 2,
                  paddingRight: 2
                }),
                menu: base => ({
                  ...base,
                  zIndex: 1056,
                  background: '#fff',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px 0 rgba(80,40,150,0.13)'
                }),
                menuPortal: base => ({ ...base, zIndex: 999999 }),
                option: (base, state) => ({
                  ...base,
                  background: state.isSelected ? '#F3EEFF' : state.isFocused ? '#F7F5FF' : '#fff',
                  color: state.isSelected ? '#7C3AED' : '#1E1147',
                  fontWeight: state.isSelected ? 600 : 400,
                  fontSize: 13.5
                })
              }}
              isSearchable={false}
            />
            </div>
          </div>
        </header>

        {/* CONTENIDO */}
        <main className="main-content"
        style={{
            overflow: isItinerario ? 'hidden' : undefined,
            display: (isItinerario || isAuthPage) ? 'flex' : 'block',
            flexDirection: (isItinerario || isAuthPage) ? 'column' : undefined,
            padding: 0,
            margin: 0,
          }}>
          {!isAuthPage && (
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
              <Breadcrumbs />
            </div>
          )}
          <Outlet />
        </main>

        {/* FOOTER: hide on itinerario page */}
        {!isItinerario && (
          <footer className="footer">
            <div className="footer-inner">
              {/* Brand */}
              <div className="footer-brand">
                <picture>
                  <source srcSet="/logo_planiaro.webp" type="image/webp" />
                  <img src="/logo_planiaro.png" alt="Planiaro" height="38" />
                </picture>
                <p>{t('footer.tagline', 'Genera itinerarios de viaje personalizados con IA en segundos. Gratis, sin registro.')}</p>
                <div className="footer-social">
                  <a href="https://twitter.com/planiaro" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i className="bi bi-twitter-x" /></a>
                  <a href="https://instagram.com/planiaro" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="bi bi-instagram" /></a>
                  <a href="https://facebook.com/planiaro" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="bi bi-facebook" /></a>
                </div>
              </div>

              {/* Producto */}
              <div className="footer-col">
                <h4>{t('footer.producto', 'Producto')}</h4>
                <ul>
                  <li><a href={`/${safeLang}`}>{t('inicio', 'Inicio')}</a></li>
                  <li><a href={`/${safeLang}/planificador-ia`}>{t('sobre_nosotros', 'Cómo funciona')}</a></li>
                  <li><a href={`/${safeLang}/viajes`}>{t('mis_viajes', 'Mis Viajes')}</a></li>
                </ul>
              </div>

              {/* Recursos */}
              <div className="footer-col">
                <h4>{t('footer.recursos', 'Recursos')}</h4>
                <ul>
                  <li><a href={`/${safeLang}/blog`}>{t('blog', 'Blog')}</a></li>
                  <li><a href={`/${safeLang}/contact`}>{t('contacto', 'Contacto')}</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="footer-col">
                <h4>{t('footer.legal', 'Legal')}</h4>
                <ul>
                  <li><a href={`/${safeLang}/privacy-policy/`}>{t('footer.privacidad', 'Privacidad')}</a></li>
                  <li><a href={`/${safeLang}/privacy-policy/`}>{t('footer.terminos', 'Términos de uso')}</a></li>
                  <li><a href={`/${safeLang}/privacy-policy/`}>{t('footer.cookies', 'Política de cookies')}</a></li>
                </ul>
              </div>
            </div>

            <div className="footer-bottom">
              <span>&copy; {new Date().getFullYear()} Planiaro. {t('footer.derechos', 'Todos los derechos reservados.')}</span>
              <span>{t('footer.hecho', 'Hecho con')} <i className="bi bi-heart-fill" style={{ color: '#7531FC', fontSize: 11 }} /> {t('footer.para', 'para viajeros del mundo')}</span>
            </div>
          </footer>
        )}
      </div>
      </ModalProvider>
    </>
  );
}
